import type { Country, Locale, Segment } from "@/types";

/**
 * The legal library.
 *
 * Two sources, and they are not the same thing. `legal/documents.json` in the
 * repository is the **registry**: what exists, who it applies to, which route
 * renders it, and which version the website prints in its footer. Firestore's
 * `legalDocuments` is what has been **published**: what an institution is asked
 * to accept and what the session gate checks.
 *
 * Publishing is the act of making the second agree with the first. Everything
 * in this file exists to make a disagreement visible before it is published
 * rather than after — an institution shown one version on the site and recorded
 * as accepting another is a compliance defect, and it would look like nothing
 * at all on both screens.
 */

export type DocType =
  | "tos"
  | "dpa"
  | "avv"
  | "ferpa_addendum"
  | "state_addendum"
  | "privacy"
  | "privacy_child"
  | "impressum"
  | "parental_consent_template"
  | "law25_addendum";

/** An entry in `legal/documents.json`, as the backend hands it over. */
export interface RegistryEntry {
  id: string;
  type: DocType;
  version: number;
  effectiveDate: string | null;
  status: "published" | "in_legal_review";
  required: boolean;
  jurisdictions: Country[];
  segments: Segment[];
  routes: Record<string, string>;
  titles: Record<string, string>;
}

/** A published version, as `legalDocuments` stores one. */
export interface PublishedDocument {
  id: string;
  type: DocType;
  jurisdictions: Country[];
  segments: Segment[];
  language: Locale;
  version: number;
  required: boolean;
  bodyPath: string;
  title: string;
  publishedAt: string;
  supersedes?: string;
}

/** i18n keys for the document types. Never the raw enum on a screen. */
export const TYPE_KEY: Record<DocType, string> = {
  tos: "legal.type.tos",
  dpa: "legal.type.dpa",
  avv: "legal.type.avv",
  ferpa_addendum: "legal.type.ferpa_addendum",
  state_addendum: "legal.type.state_addendum",
  privacy: "legal.type.privacy",
  privacy_child: "legal.type.privacy_child",
  impressum: "legal.type.impressum",
  parental_consent_template: "legal.type.parental_consent_template",
  law25_addendum: "legal.type.law25_addendum",
};

/**
 * Where a document stands.
 *
 * `diverged` is the one worth having a name for: the registry and the published
 * record disagree about the version, which means the website is printing one
 * number and the acceptance record carries another.
 */
export type DocumentState = "in_review" | "unpublished" | "current" | "behind" | "diverged";

export interface DocumentRow {
  entry: RegistryEntry;
  published: PublishedDocument | undefined;
  state: DocumentState;
  /** The version the next publication would produce. */
  nextVersion: number;
}

export function stateOf(
  entry: RegistryEntry,
  published: PublishedDocument | undefined
): DocumentState {
  // A document a lawyer has not signed off is not published, whatever else is
  // true. Publishing one would put text nobody approved behind an "I accept".
  if (entry.status === "in_legal_review") return "in_review";
  if (!published) return "unpublished";
  if (published.version === entry.version) return "current";
  if (published.version < entry.version) return "behind";
  return "diverged";
}

export function documentRows(
  registry: readonly RegistryEntry[],
  published: readonly PublishedDocument[]
): DocumentRow[] {
  const byId = new Map(published.map((document) => [document.id, document]));

  return registry.map((entry) => {
    const document = byId.get(entry.id);
    return {
      entry,
      published: document,
      state: stateOf(entry, document),
      nextVersion: (document?.version ?? 0) + 1,
    };
  });
}

/**
 * Whether the button may be pressed, and why not.
 *
 * The version is not a field on the form. The backend assigns it — one more
 * than whatever is stored — and the registry decides what the website prints.
 * When those two would not land on the same number, the fix is a commit to
 * `legal/documents.json` and a site deploy, not a text box in a console: the
 * repository is where the number is reviewed, and a console that could set it
 * would be a second authority nobody diffs.
 */
export type Publishability =
  | { publishable: true; version: number }
  | { publishable: false; reasonKey: string; version: number };

export function publishability(row: DocumentRow): Publishability {
  if (row.entry.status === "in_legal_review") {
    return { publishable: false, reasonKey: "legal.blocked.inReview", version: row.nextVersion };
  }
  if (row.nextVersion !== row.entry.version) {
    return {
      publishable: false,
      reasonKey: "legal.blocked.versionMismatch",
      version: row.nextVersion,
    };
  }
  if (!row.entry.effectiveDate) {
    return { publishable: false, reasonKey: "legal.blocked.noDate", version: row.nextVersion };
  }
  return { publishable: true, version: row.nextVersion };
}

/**
 * The publish payload, built from the registry rather than from a form.
 *
 * Nine fields typed by hand is nine chances to mistype a jurisdiction list, and
 * the mistake would publish. The console's job is to show what the registry
 * says and let somebody confirm it.
 *
 * `language` is the document's own — a German AVV is published as German even
 * though the site renders both. `bodyPath` mirrors what the seed writes, so a
 * document seeded on deploy and one published from here are the same record.
 */
export interface PublishInput {
  id: string;
  type: DocType;
  jurisdictions: Country[];
  segments: Segment[];
  language: Locale;
  required: boolean;
  bodyPath: string;
  title: string;
}

export function publishInputFor(entry: RegistryEntry, language: Locale): PublishInput {
  return {
    id: entry.id,
    type: entry.type,
    jurisdictions: entry.jurisdictions,
    segments: entry.segments,
    language,
    required: entry.required,
    bodyPath: `legal/${entry.id}`,
    title: entry.titles[language] ?? entry.titles.en ?? entry.id,
  };
}

/** The route the preview should fetch, in the language being published. */
export function routeFor(entry: RegistryEntry, locale: Locale): string | undefined {
  return entry.routes[locale] ?? entry.routes.en;
}

// ── filtering ────────────────────────────────────────────────────────────

export interface DocumentFilter {
  type?: DocType | "";
  jurisdiction?: Country | "";
  language?: Locale | "";
}

/**
 * Filtered by what the registry says, not by what is published.
 *
 * A document filtered out because it has never been published is a document
 * nobody will notice is missing, which is the failure the library exists to
 * prevent.
 */
export function filterDocuments(
  rows: readonly DocumentRow[],
  filter: DocumentFilter
): DocumentRow[] {
  return rows.filter((row) => {
    if (filter.type && row.entry.type !== filter.type) return false;
    if (filter.jurisdiction && !row.entry.jurisdictions.includes(filter.jurisdiction)) return false;
    if (filter.language && !(filter.language in row.entry.routes)) return false;
    return true;
  });
}

/** Ordered so what needs attention is at the top. */
const STATE_ORDER: Record<DocumentState, number> = {
  diverged: 0,
  unpublished: 1,
  behind: 2,
  in_review: 3,
  current: 4,
};

export function sortDocuments(rows: readonly DocumentRow[]): DocumentRow[] {
  return [...rows].sort(
    (a, b) => STATE_ORDER[a.state] - STATE_ORDER[b.state] || a.entry.id.localeCompare(b.entry.id)
  );
}

export const STATE_KEY: Record<DocumentState, string> = {
  in_review: "legal.state.inReview",
  unpublished: "legal.state.unpublished",
  current: "legal.state.current",
  behind: "legal.state.behind",
  diverged: "legal.state.diverged",
};

export const STATE_TONE: Record<DocumentState, string> = {
  in_review: "text-ink-muted",
  unpublished: "text-cue-amber",
  current: "text-cue-green",
  behind: "text-cue-amber",
  // The one that is nobody's plan: red, because it means the site and the
  // acceptance record disagree about what an institution signed.
  diverged: "text-accent",
};
